import { useEffect, useId, useRef, useState } from 'react';
import { Link } from '@inertiajs/react';
import { Wordmark } from '@/components/Wordmark';
import { Button } from '@/components/ui/Button';
import { ThemeToggle } from '@/components/ThemeToggle';
import { navigation } from '@/data/navigation';
import { useActivePath } from '@/hooks/useActivePath';
import { trackUmamiEvent } from '@/lib/analytics';

/**
 * Sticky site header: Wordmark + desktop text nav (`≥960px`) + solid CTA, or
 * Wordmark + hamburger on mobile. Owns the full-screen mobile drawer and its
 * open/close behavior (scroll-lock, Esc, focus management), matching the
 * mockup's `header`/`.bar`/`nav.desk`/`.burger`/`.drawer` + inline `<script>`.
 *
 * The drawer is rendered as a *sibling* of `<header>`, not nested inside it:
 * the header's `backdrop-blur` establishes a new containing block for
 * `position: fixed` descendants (per spec, `filter`/`backdrop-filter` do
 * this just like `transform`), which would otherwise confine the drawer's
 * `fixed inset-0` to the header's own 64px box instead of the viewport.
 */
export const SiteHeader = () => {
  const { isActive } = useActivePath();
  const [open, setOpen] = useState(false);
  const hamburgerRef = useRef<HTMLButtonElement>(null);
  const closeRef = useRef<HTMLButtonElement>(null);
  const wasOpen = useRef(false);
  const drawerId = useId();

  // Lock body scroll + listen for Esc while the drawer is open; move focus
  // into the drawer on open; contain Tab within the drawer by marking every
  // other top-level element (header, main, footer, sticky CTA — all mounted
  // as siblings under #app) `inert` for the duration, so `aria-modal` holds
  // in practice, not just in name. Cleans up on close and on unmount.
  useEffect(() => {
    if (!open) return undefined;

    const appRoot = document.getElementById('app');
    const containedSiblings = appRoot
      ? Array.from(appRoot.children).filter((element) => element.id !== drawerId)
      : [];
    containedSiblings.forEach((element) => element.setAttribute('inert', ''));

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    closeRef.current?.focus();

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false);
    };
    document.addEventListener('keydown', onKeyDown);

    return () => {
      containedSiblings.forEach((element) => element.removeAttribute('inert'));
      document.body.style.overflow = previousOverflow;
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open, drawerId]);

  // Restore focus to the hamburger button whenever the drawer transitions
  // from open -> closed (Esc, close button, or nav-link click).
  useEffect(() => {
    if (wasOpen.current && !open) {
      hamburgerRef.current?.focus();
    }
    wasOpen.current = open;
  }, [open]);

  const closeDrawer = () => setOpen(false);

  return (
    <>
      <header className="bg-background/88 border-line-soft sticky top-0 z-40 border-b backdrop-blur">
        <div className="mx-auto flex h-16 max-w-[1120px] items-center justify-between px-5 md:px-10">
          <Wordmark />

          <div className="flex items-center gap-2">
            <nav className="hidden items-center gap-7.5 min-[960px]:flex">
              {navigation.map((item) => (
                <Link
                  key={item.path}
                  href={item.path}
                  className={`text-sm transition-colors duration-150 ${
                    isActive(item.path) ? 'text-ink' : 'text-ink-soft hover:text-ink'
                  }`}
                >
                  {item.name}
                </Link>
              ))}
              <Button
                as="link"
                href="/contact"
                variant="solid"
                className="ml-1"
                onClick={() => trackUmamiEvent('cta_request_visit', { location: 'header' })}
              >
                Request a visit
              </Button>
            </nav>

            <ThemeToggle />

            <button
              ref={hamburgerRef}
              type="button"
              aria-label="Open menu"
              aria-expanded={open}
              aria-controls={drawerId}
              onClick={() => setOpen(true)}
              className="hover:bg-surface text-ink grid size-11 place-items-center rounded-[3px] min-[960px]:hidden"
            >
              <svg
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth={1.6}
                className="size-[22px]"
              >
                <path d="M3 7h18M3 12h18M3 17h18" strokeLinecap="round" />
              </svg>
            </button>
          </div>
        </div>
      </header>

      <div
        id={drawerId}
        role="dialog"
        aria-modal={open}
        aria-label="Site navigation"
        aria-hidden={!open}
        inert={!open}
        className={`bg-background fixed inset-0 z-[60] flex flex-col p-5 transition-transform duration-[320ms] ease-[cubic-bezier(0.4,0,0.2,1)] motion-reduce:transition-none ${
          open ? 'visible translate-y-0' : 'invisible -translate-y-full'
        }`}
      >
        <div className="flex h-16 items-center justify-between">
          <Wordmark onClick={closeDrawer} />
          <div className="flex items-center gap-2">
            <ThemeToggle />
            <button
              ref={closeRef}
              type="button"
              aria-label="Close menu"
              onClick={closeDrawer}
              className="hover:bg-surface text-ink grid size-11 place-items-center rounded-[3px]"
            >
              <svg
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth={1.6}
                className="size-[22px]"
              >
                <path d="M6 6l12 12M18 6L6 18" strokeLinecap="round" />
              </svg>
            </button>
          </div>
        </div>

        <nav className="mt-5.5 flex flex-col gap-1">
          {navigation.map((item) => (
            <Link
              key={item.path}
              href={item.path}
              onClick={closeDrawer}
              className="font-display border-line-soft border-b py-3 text-[30px]"
            >
              {item.name}
            </Link>
          ))}
        </nav>

        <div className="mt-auto flex flex-col gap-3.5 pt-6">
          <Button
            as="link"
            href="/contact"
            variant="solid"
            block
            onClick={() => {
              trackUmamiEvent('cta_request_visit', { location: 'menu' });
              closeDrawer();
            }}
          >
            Request a visit
          </Button>
          <a
            href="mailto:hello@puretouchbydaniela.com"
            onClick={() => trackUmamiEvent('email_click', { location: 'menu' })}
            className="text-ink-soft text-[12.5px] tracking-[.03em]"
          >
            hello@puretouchbydaniela.com
          </a>
        </div>
      </div>
    </>
  );
};
